import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { filtersAtom } from "app/state"
import { TSlider } from "components/slider"
import { useAtomValue, useSetAtom } from "jotai"
import type { Filter } from "types/filter"
import { Facet, range } from "data/facets"
import { LegendBackHeader } from "./legend-back-header"
import { LevelName } from "app/constants"
import { FacetTooltip } from "./facet-tooltip"
import { FacetName } from "./facet-name"
import { City } from "@prisma/client"
import { FACET_VALUES_TO_DISPLAY_NAME_DICTIONARY } from "../legend.constants"
type Either = "either" | "yes" | "no"

const getFacetValueLabel = (
  value: string | number,
  t: (key: string) => string,
  dictionary?: Record<string, string>
) => {
  const displayValue = dictionary
    ? Object.entries(dictionary).find(([, rawValue]) => rawValue === value)?.[0] || value
    : value

  if (displayValue === "Yes") return t("common:yes")
  if (displayValue === "No") return t("common:no")

  return displayValue
}

function FilterUI({ facet, filter, city }: { facet: Facet; filter: Filter; city?: City }) {
  const setFilters = useSetAtom(filtersAtom)
  const { t, i18n } = useTranslation(["facets", "common"])
  const isSpanish = i18n.language === "es"
  const [selectedSublayer, setSelectedSublayer] = useState<Facet>()
  useEffect(() => {
    if (facet.sublayers) {
      setSelectedSublayer(facet.sublayers[0])
    }
  }, [facet])
  if (facet.sublayers) {
    return (
      <div key={facet.field.field} className="flex items-start justify-between">
        <div className="mb-2 flex items-center justify-between pr-1 leading-tight">
          {facet.title === "merged_shade_facet" && (
            <div className="pr-1 text-[11px] leading-tight">
              {t("facets:merged_shade_facet.name")}
            </div>
          )}
          <div className="px-1">
            <select
              className="w-20 rounded border-gray-400 py-1 pl-[5px] pr-[2px] text-sm focus:border-gray-400 focus:ring-0"
              value={selectedSublayer?.title}
              onChange={(e) => {
                const newSublayer = facet.sublayers!.find((s) => s.title === e.target.value)!
                const sublayerData = newSublayer.data as range
                setSelectedSublayer(newSublayer)
                // Remove previous sublayer filter
                setFilters((filters) => {
                  const newFilters = new Map(filters)
                  newFilters.set(facet, {
                    type: "range",
                    sublayer: newSublayer.field.field,
                    value: sublayerData.value,
                    active: false,
                  })
                  return newFilters
                })
              }}
            >
              {facet.sublayers.map((sublayer) => (
                <option key={sublayer.title} value={sublayer.title}>
                  <FacetName facetKey={sublayer.field.field} city={city} />
                </option>
              ))}
            </select>
          </div>
          <FacetTooltip facetKey={facet.title} city={city} />
        </div>
        {selectedSublayer?.data?.type === "range" && (
          <div className="w-[53%] px-1">
            <TSlider
              showInputs
              min={selectedSublayer.data.formatter!.dataToLabel(selectedSublayer.data.value[0])}
              max={selectedSublayer.data.formatter!.dataToLabel(selectedSublayer.data.value[1])}
              step={selectedSublayer.data.formatter!.dataToLabel(selectedSublayer.data.step)}
              valueLabelDisplay="off"
              valueLabelFormat={selectedSublayer.formatter}
              onChange={(_event, val) => {
                const value = val as [number, number]
                const facetData = selectedSublayer.data as range
                setFilters((filters) => {
                  const newFilters = new Map(filters)
                  const formattedEndRange = [
                    facetData.formatter!.dataToLabel(facetData.value[0]),
                    facetData.formatter!.dataToLabel(facetData.value[1]),
                  ]

                  const active = !(
                    formattedEndRange[0] === value[0] && formattedEndRange[1] === value[1]
                  )
                  const formattedValue = facetData.formatter
                    ? [
                        facetData.formatter.labelToData(value[0]),
                        facetData.formatter.labelToData(value[1]),
                      ]
                    : value
                  newFilters.set(facet, {
                    type: "range",
                    sublayer: selectedSublayer.field.field,
                    value: formattedValue as [number, number],
                    active,
                  })
                  return newFilters
                })
              }}
              value={
                selectedSublayer.data.formatter
                  ? [
                      selectedSublayer.data.formatter.dataToLabel(
                        (filter.value || selectedSublayer.data.value)[0] as number
                      ),
                      selectedSublayer.data.formatter.dataToLabel(
                        (filter.value || selectedSublayer.data.value)[1] as number
                      ),
                    ]
                  : ((filter.value || selectedSublayer.data.value) as [number, number])
              }
            />
          </div>
        )}
      </div>
    )
  }
  if (facet.data.type === "rangeChoice") {
    return (
      <div key={facet.field.field} className="flex items-start justify-between">
        <div className="pr-2 leading-tight">
          <FacetName facetKey={facet?.field?.field} city={city} />
          <FacetTooltip facetKey={facet?.field?.field} city={city} />
        </div>
        <div className="flex flex-row px-1">
          {facet.data.value.map((val) => (
            <button
              key={val.label}
              className={`ml-1 flex h-8 items-center justify-center rounded border border-brand-green p-1 text-xs ${
                filter.value === val.range
                  ? "bg-brand-green text-white"
                  : "text-brand-green hover:border-brand-green-dark hover:text-brand-green-dark"
              }`}
              onClick={() => {
                setFilters((filters) => {
                  const newFilters = new Map(filters)
                  newFilters.set(facet, {
                    type: "range",
                    value: val.range as [number, number],
                    active: true,
                  })
                  return newFilters
                })
              }}
            >
              {val.label}
            </button>
          ))}
        </div>
      </div>
    )
  }
  if (facet.data.type === "boolean") {
    return (
      <div key={facet.field.field} className="flex items-start justify-between">
        <div className="pr-2 leading-tight">
          <FacetName facetKey={facet.field.field} isFilter={true} city={city} />
          <FacetTooltip facetKey={facet.field.field} city={city} />
        </div>
        <div className="px-1">
          <select
            className="w-24 rounded border-gray-400 py-1 text-sm focus:border-gray-400 focus:ring-0"
            value={(filter.value || "either") as Either}
            onChange={(e) => {
              setFilters((filters) => {
                const newFilters = new Map(filters)
                newFilters.set(facet, {
                  type: "switch",
                  value: e.target.value as Either,
                  active: e.target.value !== "either",
                })
                return newFilters
              })
            }}
          >
            <option value="either">{t("common:any")}</option>
            <option value="yes">{t("common:yes")}</option>
            <option value="no">{t("common:no")}</option>
          </select>
        </div>
      </div>
    )
  }
  if (facet.data.type === "enum") {
    const dictionary = city && FACET_VALUES_TO_DISPLAY_NAME_DICTIONARY[city]?.[facet.field.field]
    return (
      <div key={facet.field.field} className="flex items-start justify-between">
        <div className="pr-2 leading-tight">
          <FacetName facetKey={facet.field.field} isFilter={true} city={city} />
          <FacetTooltip facetKey={facet.field.field} city={city} />
        </div>
        <div className="px-1">
          <select
            className="w-24 rounded border-gray-400 py-1 text-sm focus:border-gray-400 focus:ring-0"
            value={(filter.value || "") as string}
            onChange={(e) => {
              setFilters((filters) => {
                const newFilters = new Map(filters)
                newFilters.set(facet, {
                  type: "enum",
                  value: e.target.value ? e.target.value : null,
                  active: e.target.value ? true : false,
                })
                return newFilters
              })
            }}
          >
            <option value="">{t("common:any")}</option>
            {facet.data.values.map((val) => (
              <option key={val} value={val}>
                {getFacetValueLabel(val, t, dictionary)}
              </option>
            ))}
          </select>
        </div>
      </div>
    )
  }

  return (
    <div
      key={facet.field.field}
      className="grid items-start"
      style={{
        gridTemplateColumns: isSpanish ? "minmax(0, 1fr) 150px" : "minmax(0, 1fr) 180px",
      }}
    >
      <div className="leading-tight">
        <FacetName facetKey={facet.field.field} isFilter={true} city={city} />
        <FacetTooltip facetKey={facet.field.field} city={city} />
      </div>
      <div className="pl-2">
        <TSlider
          showInputs
          min={
            facet.data.type == "range" && facet.data.formatter
              ? facet.data.formatter.dataToLabel(facet.data.value[0])
              : facet.data.value[0]
          }
          max={
            facet.data.type == "range" && facet.data.formatter
              ? facet.data.formatter.dataToLabel(facet.data.value[1])
              : facet.data.value[1]
          }
          step={
            facet.data.type == "range" && facet.data.formatter
              ? facet.data.formatter.dataToLabel(facet.data.step)
              : facet.data.step
          }
          valueLabelDisplay="off"
          valueLabelFormat={(val) => {
            return facet.formatter ? facet.formatter(val) : val
          }}
          onChange={(_event, val) => {
            const value = val as [number, number]
            setFilters((filters) => {
              const newFilters = new Map(filters)
              let formattedEndRange!: [number, number]
              if (facet.data.type === "range") {
                if (facet.data.formatter) {
                  formattedEndRange = [
                    facet.data.formatter.dataToLabel(facet.data.value[0]),
                    facet.data.formatter.dataToLabel(facet.data.value[1]),
                  ]
                } else {
                  formattedEndRange = facet.data.value
                }
              }
              const active =
                facet.data.type == "range" &&
                !(formattedEndRange[0] === value[0] && formattedEndRange[1] === value[1])
              const formattedValue =
                facet.data.type == "range" && facet.data.formatter
                  ? [
                      facet.data.formatter.labelToData(value[0]),
                      facet.data.formatter.labelToData(value[1]),
                    ]
                  : value
              newFilters.set(facet, {
                type: "range",
                value: formattedValue as [number, number],
                active,
              })
              return newFilters
            })
          }}
          value={
            facet.data.type == "range" && facet.data.formatter
              ? [
                  facet.data.formatter.dataToLabel((filter.value as [number, number])[0]),
                  facet.data.formatter.dataToLabel((filter.value as [number, number])[1]),
                ]
              : (filter.value as [number, number])
          }
        />
      </div>
    </div>
  )
}

export const FiltersPanel = ({
  currentLevel,
  onModeChange,
  city,
}: {
  currentLevel: LevelName
  onModeChange: React.Dispatch<React.SetStateAction<"facets" | "filters" | null>>
  city?: City
}) => {
  const { t } = useTranslation(["common"])
  const filters = useAtomValue(filtersAtom)
  return (
    <div className="w-full">
      <div>
        <LegendBackHeader title={t("common:filters")} onClick={() => onModeChange(null)} />
        <div className="space-y-2 p-3">
          {Array.from(filters.entries())
            .filter(
              ([facet]) =>
                facet.field?.type === (currentLevel == "Parcel" ? "Area" : currentLevel) &&
                !facet.hideFromFilters
            )
            .map(([facet, filter], i) => {
              return <FilterUI key={i} facet={facet} filter={filter} city={city} />
            })}
        </div>
      </div>
    </div>
  )
}
